import {
  Box,
  Button,
  Checkbox,
  Dialog,
  DialogActions,
  DialogContent,
  FormControlLabel,
  FormLabel,
} from '@mui/material';
import { useSnackbar } from 'notistack';
import React, { useCallback } from 'react';
import { useTranslation } from 'react-i18next';

import { useLoggedInUserRoles } from '~/components/Auth';
import { SolaForceDialogTitle } from '~/components/SolaForceDialogTitle';
import { useEmployeeProfile } from '~/data/employeeProfile';
import { Roles } from '~/data/user';
import { AccountManagementMenu_OnUpdateLegacy } from '~/library/AccountManagementMenu/component';
import { isRoleHRAdmin, isRoleHRAdminFull } from '~/services/Permissions';

import { useChangeRole } from './useChangeRole';

type Props = AccountManagementMenu_OnUpdateLegacy & {
  open: boolean;
  onClose: () => void;
};

export const ChangeRole = ({ open, onClose, onUpdateLegacy }: Props) => {
  const { t } = useTranslation();
  const { enqueueSnackbar } = useSnackbar();
  const { profile } = useEmployeeProfile();
  const getTitleWithEmployeeName = useCallback(
    (title) =>
      !!profile ? `${title} - ${profile.firstName} ${profile.lastName}` : title,
    [profile],
  );

  const onSubmitSuccess = useCallback(() => {
    if (!!onUpdateLegacy) {
      onUpdateLegacy(['changeRole']);
    }
    onClose();
  }, [onUpdateLegacy, onClose]);

  const { handleChange, onSubmit, empRoles } = useChangeRole({
    onSubmitSuccess,
  });
  const roles = useLoggedInUserRoles();
  const handleFormSubmit = () => {
    if (Object.values(empRoles).every((roles) => !roles)) {
      enqueueSnackbar(t('laFillManFields'), {
        variant: 'error',
      });
    } else {
      onSubmit();
    }
  };

  const RoleOption = (roleProps: {
    checked?: boolean;
    role: Roles;
    label: string;
  }) => {
    return (
      <Box>
        <FormControlLabel
          control={
            <Checkbox
              name={'fUserRoles'}
              checked={roleProps.checked}
              value={roleProps.role}
              onChange={handleChange}
            />
          }
          label={roleProps.label}
        />
      </Box>
    );
  };

  return (
    <Dialog open={open} onClose={onClose}>
      <SolaForceDialogTitle onClose={onClose}>
        {getTitleWithEmployeeName(t('laChangeRole'))}
      </SolaForceDialogTitle>
      <DialogContent dividers={true}>
        <FormLabel>{t('laRole')}</FormLabel>

        <RoleOption
          role={Roles.EMPLOYEE}
          checked={empRoles.EMPLOYEE}
          label={t('laEmployee')}
        />
        <RoleOption
          role={Roles.IT_ADMIN}
          checked={empRoles.IT_ADMIN}
          label={t('laITAdmin')}
        />
        <RoleOption
          role={Roles.DEPUTY_MANAGER}
          checked={empRoles.DEPUTY_MANAGER}
          label={t('laDeputyManager')}
        />
        <RoleOption
          role={Roles.ALT_MANAGER}
          checked={empRoles.ALT_MANAGER}
          label={t('laAltManager')}
        />
        <RoleOption
          role={Roles.MANAGER}
          checked={empRoles.MANAGER}
          label={t('laManager')}
        />
        <RoleOption
          role={Roles.HR_DEV}
          checked={empRoles.HR_DEV}
          label={t('laHRDev')}
        />
        <RoleOption
          role={Roles.HR_STAFF}
          checked={empRoles.HR_STAFF}
          label={t('laHR')}
        />
        {isRoleHRAdminFull(roles) && (
          <RoleOption
            role={Roles.HR_ADMIN}
            checked={empRoles.HR_ADMIN}
            label={t('laHRAdmin')}
          />
        )}
        {isRoleHRAdmin(roles) && (
          <RoleOption
            role={Roles.HR_ADMIN_LIMITED}
            checked={empRoles.HR_ADMIN_LIMITED}
            label={t('laHRAdminLimited')}
          />
        )}
        <RoleOption
          role={Roles.HR_SALARY}
          checked={empRoles.HR_SALARY}
          label={t('laHRSalary')}
        />
        <RoleOption
          role={Roles.RECRUITER}
          checked={empRoles.RECRUITER}
          label={t('laRecruiter')}
        />
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} color={'secondary'}>
          {t('cancel')}
        </Button>
        <Button onClick={handleFormSubmit}>{t('save')}</Button>
      </DialogActions>
    </Dialog>
  );
};
